'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { stegaClean } from '@sanity/client/stega';
import { cn } from '@/lib/utils';
import type { IYoutubeLiveWidget } from '@/sanity/lib/definitions';
import { getYouTubeEmbedUrl } from '@/lib/youtube';

/**
 * "IQ Capital TV" corner widget — a static YouTube embed, always shown while
 * enabled (no schedule/countdown, unlike LaunchEventWidget). Built 1:1 from
 * Figma node 9293:13323 (IQ Capital | New UI 2026), sizes are Figma px / 8
 * (see app/globals.scss).
 *
 * Shares the bottom-left corner with LaunchEventWidget/PayoutToast — PageHomeJSX
 * decides which one renders.
 *
 * Desktop-only for now (hidden below xl/1280px): no mobile/tablet design has
 * been agreed on yet, so hiding it there avoids covering page content with a
 * layout that hasn't been signed off.
 */

function WatchIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden className="h-[2rem] w-[2rem]">
      <path
        d="M4 12L12 4M12 4H5.5M12 4V10.5"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface YoutubeLiveWidgetProps {
  data?: IYoutubeLiveWidget | null;
  className?: string;
}

export default function YoutubeLiveWidget({ data, className }: YoutubeLiveWidgetProps) {
  const cleaned = stegaClean(data ?? null) as IYoutubeLiveWidget | null;

  if (!cleaned?.isEnabled || !cleaned?.youtubeUrl) return null;

  const embedUrl = getYouTubeEmbedUrl(cleaned.youtubeUrl);
  const channelLabel = cleaned.channelLabel || 'IQ Capital TV';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ type: 'spring', stiffness: 110, damping: 20, delay: 0.6 }}
        className={cn('fixed bottom-[1.75rem] left-[1.75rem] z-50 hidden xl:flex', className)}
      >
        <div className="flex w-[48rem] flex-col gap-[1.375rem] rounded-[0.75rem] border border-solid border-white/10 bg-[#001b14] px-[1.25rem] pb-[1.25rem] pt-[1.75rem] shadow-[0px_3.5rem_3.5rem_0px_rgba(0,27,20,0.17)]">
          <div className="flex h-[2.5rem] w-full items-center gap-[1.5rem]">
            <div className="flex min-w-0 flex-1 items-center gap-[1rem]">
              <p className="mb-0 whitespace-nowrap font-geist text-[2rem] font-medium leading-[2.5rem] tracking-[-0.02rem] text-white/80">
                {channelLabel}
              </p>
              <div className="relative flex shrink-0 items-center gap-[0.75rem] rounded-[5rem] border border-solid border-[rgba(255,160,160,0.3)] px-[1.25rem] py-[0.625rem]">
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 rounded-[5rem] bg-[rgba(255,59,59,0.16)]"
                />
                <span className="relative h-[0.75rem] w-[0.75rem] shrink-0 rounded-[0.375rem] bg-[#ff3b3b]" />
                <p className="relative mb-0 whitespace-nowrap font-geist text-[1.75rem] font-semibold leading-[1.75rem] tracking-[0.035rem] text-[#ff9696]">
                  LIVE
                </p>
              </div>
            </div>

            {cleaned.youtubeChannelUrl && (
              <a
                href={cleaned.youtubeChannelUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex shrink-0 items-center gap-[0.5rem] text-white/70 transition-colors hover:text-white"
              >
                <span className="whitespace-nowrap font-geist text-[1.75rem] leading-[2.5rem]">
                  Watch on YouTube
                </span>
                <WatchIcon />
              </a>
            )}
          </div>

          <div className="relative h-[24.609rem] w-full overflow-hidden rounded-[0.5rem] bg-black">
            <iframe
              src={embedUrl}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="absolute inset-0 h-full w-full"
            />
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
